const _ = require("lodash");
const {drawCurlyBrace} = require("../util/RenderHelpers");
const {drawBatexAtCenter} = require("./CommonHelpers");
const colors = require("../../util/ColorUtil").getDiagramColors();

const formatBatex = function(batex, fontSize, color) {
	let formatedBatex = batex;
	// determine whether batex being wrapped is in math mode
	const isMath = batex.startsWith("$") && batex.endsWith("$");
	// remove math mode if it exists (it will be added back below)
	if (isMath) {
		formatedBatex = batex.slice(1, -1);
	}

	// apply \bigger and \color commands to format, and reapply math mode if necessary
	formatedBatex = `${isMath ? "$" : ""}${
		fontSize < 1 ? `\\smaller[${1 / fontSize}]` : `\\bigger[${fontSize}]`
	}{\\color{${color}}{${formatedBatex}}}${isMath ? "$" : ""}`;

	return formatedBatex;
};

module.exports = function(ctx, x, y, props) {
	let {xScale, yScale, height, cells, braces, setSize} = props;
	xScale = xScale || 1;
	yScale = yScale || 1;
	cells = cells || [];
	braces = braces || [];
	setSize = setSize === false ? false : true;

	// default values for each cell
	const defaultCell = {
		width: 1,
		content: "$\\blank{1}$",
		color: "black",
		bgColor: "white",
		quantity: 1,
		dashed: false,
		selfContained: false,
		fontSize: 0.9,
		contentOffset: 0,
	};

	// set default values for each cell and duplicate them until each quantity is met
	let fullLength = 0;
	const updatedCells = [];
	for (let cellTypeIndex = 0; cellTypeIndex < cells.length; cellTypeIndex++) {
		let cell = {...defaultCell, ...cells[cellTypeIndex]};
		for (let cellIndex = 0; cellIndex < cell.quantity; cellIndex++) {
			if (cell.selfContained && cellIndex === cell.quantity - 1) {
				cell.dashed = false;
			}
			cell.content = formatBatex(cell.content, cell.fontSize, cell.color);

			updatedCells.push(cell);
			fullLength += cell.width;
			cell = {...defaultCell, ...cells[cellTypeIndex]};
		}
	}
	cells = updatedCells;

	// appply these default values to each brace
	const defaultBrace = {
		content: "$\\blank{2}$",
		fromIndex: 0,
		length: cells.length,
		position: "top",
		fontSize: 1,
		color: "black",
		boxSize: 0,
		boxPadding: 5,
	};
	for (let i = 0; i < braces.length; i++) {
		braces[i] = {...defaultBrace, ...braces[i]};
		braces[i].boxSize =
			braces[i].boxSize || (braces[i].content === "$\\blank{2}$" ? 50 : 27);
	}

	// find the height of the diagram
	if (height === "shorter") {
		height = 35;
	} else if (height === "taller") {
		height = 65;
	} else {
		height = 50;
	}

	// initial diagram attributes
	const diagramMargin = 2;
	x += diagramMargin;
	const width = 96 * 3 * xScale; // 96 ~ 1 inch, default size is 3 inches
	height *= yScale;
	const lineWidth = 2;
	const braceHeight = 16;

	// resize the screen if necessary
	const largestBraceBoxTop =
		_.max(
			braces.map(brace =>
				brace.position === "bottom" ? 0 : brace.boxSize + brace.boxPadding
			)
		) || 0;
	const largestBraceBoxBottom =
		_.max(
			braces.map(brace =>
				brace.position === "bottom" ? brace.boxSize + brace.boxPadding + 2 : 0
			)
		) || 0;
	const newWidth = width + x + 2 * lineWidth + diagramMargin;
	let newHeight =
		height +
		2 * lineWidth +
		largestBraceBoxTop +
		largestBraceBoxBottom +
		2 * diagramMargin;
	y += lineWidth + diagramMargin;
	if (braces.some(brace => brace.position !== "bottom")) {
		newHeight += braceHeight;
		y += largestBraceBoxTop + braceHeight;
	}
	if (braces.some(brace => brace.position === "bottom")) {
		newHeight += braceHeight;
	}
	if (setSize) ctx.setSize(newWidth, newHeight);

	ctx.strokeStyle = "black";
	ctx.save();

	// draw each block in the tape diagram
	let currX = 2;
	for (let i = 0; i < cells.length; i++) {
		const cellWidth = (cells[i].width * width) / fullLength;
		currX += cellWidth;

		// draw in the background color
		ctx.fillStyle = colors[cells[i].bgColor] || "white";
		ctx.fillRect(x + currX - cellWidth, y, cellWidth, height);

		// draw in batex for each cell
		ctx.restore();
		drawBatexAtCenter(
			ctx,
			cells[i].content,
			x + currX - cellWidth / 2 - 1,
			y + height / 2 + cells[i].contentOffset + diagramMargin
		);
		ctx.save();

		// draw the cell's outline, either dashed or solid
		if (i !== cells.length - 1) {
			if (cells[i].dashed) {
				ctx.lineWidth = (4 * lineWidth) / 3;
				ctx.setLineDash([4, 2]);
			} else {
				ctx.lineWidth = 2 * lineWidth;
				ctx.setLineDash([]);
			}
			ctx.beginPath();
			ctx.moveTo(x + currX, y);
			ctx.lineTo(x + currX, y + height);
			ctx.stroke();
		}
	}

	// draw the entire box's outline
	ctx.lineWidth = lineWidth;
	ctx.beginPath();
	ctx.strokeRect(x + ctx.lineWidth / 2, y + ctx.lineWidth / 2, width, height);
	ctx.closePath();

	// draw each curly brace in
	for (let i = 0; i < braces.length; i++) {
		const brace = braces[i];
		let braceX = x + 2;
		let braceLength = -9;

		// calculate full length and starting x value
		for (
			let cellIndex = 0;
			cellIndex < Math.min(brace.fromIndex + brace.length, cells.length);
			cellIndex++
		) {
			if (cellIndex < brace.fromIndex) {
				braceX += (cells[cellIndex].width * width) / fullLength;
			} else {
				braceLength += (cells[cellIndex].width * width) / fullLength;
			}
		}

		ctx.lineWidth = lineWidth;

		drawCurlyBrace(
			ctx,
			braceLength,
			braceX,
			brace.position === "bottom" ? y + height + 5 : y - 3,
			brace.position === "bottom" ? "b" : "t"
		);

		// draw brace contents
		if (brace.content === "$\\blank{2}$") {
			ctx.beginPath();
			ctx.strokeRect(
				braceX + braceLength / 2 - brace.boxSize / 2,
				brace.position === "bottom"
					? y + height + braceHeight + brace.boxPadding + 2
					: y - braceHeight - brace.boxSize - brace.boxPadding,
				brace.boxSize,
				brace.boxSize
			);
			ctx.closePath();
		}

		// draw in batex for each brace
		brace.content = formatBatex(brace.content, brace.fontSize, brace.color);
		ctx.restore();
		drawBatexAtCenter(
			ctx,
			brace.content,
			braceX + braceLength / 2,
			brace.position === "bottom"
				? y + height + braceHeight + brace.boxSize / 2 + brace.boxPadding + 2
				: y - braceHeight - brace.boxSize / 2 - brace.boxPadding
		);
		ctx.save();
	}

	ctx.restore();
};
